import { ToolValidationCard } from "@app/components/actions/blocked/ToolValidationCard";
import { useBlockedActionsContext } from "@app/components/assistant/conversation/BlockedActionsProvider";
import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import {
  EditableToolValidation,
  isEditableToolValidationSupported,
} from "@app/components/assistant/conversation/editable_tool_validation/EditableToolValidation";
import { GmailSendMailSummaryCard } from "@app/components/assistant/conversation/editable_tool_validation/gmail/tools/GmailSendMailSummaryCard";
import type { ValidationRequiredToolExecution } from "@app/components/assistant/conversation/editable_tool_validation/types";
import type { MCPValidationOutputType } from "@app/lib/actions/constants";
import {
  GMAIL_SEND_MAIL_TOOL_NAME,
  GMAIL_TOOL_NAME,
} from "@app/lib/api/actions/servers/gmail/metadata";
import { isGmailSendMailInput } from "@app/lib/api/actions/servers/gmail/types";
import { canCurrentUserRespondToParentUserMessage } from "@app/lib/api/assistant/conversation/can_current_user_respond";
import { useAuth } from "@app/lib/auth/AuthContext";
import { useValidateAction } from "@app/lib/swr/tool_actions";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import { useEffect, useMemo, useState } from "react";

// Once per page load, so closing the review pane is respected across remounts of the card.
const autoOpenedReviewActionIds = new Set<string>();

interface MCPToolValidationRequiredProps {
  triggeringUser: UserType | null;
  owner: LightWorkspaceType;
  blockedAction: ValidationRequiredToolExecution;
  conversationId?: string | null;
  presentation?: "inline" | "panel";
  onResolved?: () => void;
}

export function MCPToolValidationRequired({
  triggeringUser,
  owner,
  blockedAction,
  conversationId,
  presentation = "inline",
  onResolved,
}: MCPToolValidationRequiredProps) {
  const { user } = useAuth();
  const { openPanel, hasPanelHost } = useConversationSidePanelContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    getBlockedActions,
    removeCompletedAction,
    isActionPulsing,
    stopPulsingAction,
  } = useBlockedActionsContext();
  const { validateAction, isValidating } = useValidateAction({
    owner,
    onError: setErrorMessage,
  });

  const canCurrentUserRespond = useMemo(
    () =>
      canCurrentUserRespondToParentUserMessage({
        parentUserId: blockedAction.userId,
        currentUserId: user?.sId,
      }),
    [blockedAction.userId, user?.sId]
  );

  const isPulsing = isActionPulsing(blockedAction.actionId);

  const shouldUseEditableToolValidation =
    canCurrentUserRespond && isEditableToolValidationSupported(blockedAction);

  const gmailSendInputs =
    blockedAction.metadata.mcpServerName === GMAIL_TOOL_NAME &&
    blockedAction.metadata.toolName === GMAIL_SEND_MAIL_TOOL_NAME &&
    isGmailSendMailInput(blockedAction.inputs)
      ? blockedAction.inputs
      : null;

  const canReviewInPanel =
    presentation === "inline" &&
    hasPanelHost &&
    shouldUseEditableToolValidation;

  const showGmailSummary =
    presentation === "inline" &&
    canCurrentUserRespond &&
    gmailSendInputs !== null &&
    (canReviewInPanel || !shouldUseEditableToolValidation);

  const openReview = () =>
    openPanel({ type: "tool_review", actionId: blockedAction.actionId });

  useEffect(() => {
    if (
      !showGmailSummary ||
      !canReviewInPanel ||
      autoOpenedReviewActionIds.has(blockedAction.actionId)
    ) {
      return;
    }
    autoOpenedReviewActionIds.add(blockedAction.actionId);
    openPanel({ type: "tool_review", actionId: blockedAction.actionId });
  }, [showGmailSummary, canReviewInPanel, blockedAction.actionId, openPanel]);

  const handleValidationStart = () => {
    stopPulsingAction(blockedAction.actionId);
    setErrorMessage(null);
  };

  const completeAction = () => {
    removeCompletedAction(blockedAction.actionId);
    onResolved?.();
  };

  const handleValidation = async (
    approved: MCPValidationOutputType
  ): Promise<boolean> => {
    handleValidationStart();

    const result = await validateAction({
      contextType: "agent_loop",
      conversationId: blockedAction.conversationId,
      messageId: blockedAction.messageId,
      actionId: blockedAction.actionId,
      approved,
    });

    if (!result.success) {
      setErrorMessage("Failed to assess action approval. Please try again.");
      return false;
    }
    completeAction();

    // When the user grants always-allow, cascade to other queued
    // confirmations of the same tool so they don't have to click each one.
    if (
      approved === "always_approved" &&
      user &&
      !blockedAction.inTaskConversation
    ) {
      const cascadable = getBlockedActions(user.sId).filter(
        (c) =>
          c.actionId !== blockedAction.actionId &&
          c.status === "blocked_validation_required" &&
          c.metadata.mcpServerName === blockedAction.metadata.mcpServerName &&
          c.metadata.toolName === blockedAction.metadata.toolName
      );

      for (const cascadeAction of cascadable) {
        const cascadeResult = await validateAction({
          contextType: "agent_loop",
          conversationId: cascadeAction.conversationId,
          messageId: cascadeAction.messageId,
          actionId: cascadeAction.actionId,
          approved: "approved",
        });
        if (cascadeResult.success) {
          removeCompletedAction(cascadeAction.actionId);
        }
      }
    }

    return true;
  };

  if (showGmailSummary && gmailSendInputs) {
    return (
      <GmailSendMailSummaryCard
        inputs={gmailSendInputs}
        isValidating={isValidating}
        isPulsing={isPulsing}
        errorMessage={errorMessage}
        onSend={() => void handleValidation("approved")}
        onDiscard={() => void handleValidation("rejected")}
        onReview={canReviewInPanel ? openReview : undefined}
      />
    );
  }

  if (shouldUseEditableToolValidation) {
    return (
      <>
        <EditableToolValidation
          blockedAction={blockedAction}
          owner={owner}
          isPulsing={isPulsing}
          isValidating={isValidating}
          onActionCompleted={completeAction}
          onError={setErrorMessage}
          onValidationStart={handleValidationStart}
        />
        {errorMessage && (
          <div className="mt-2 text-sm font-medium text-warning-800">
            {errorMessage}
          </div>
        )}
      </>
    );
  }

  return (
    <ToolValidationCard
      validationRequest={blockedAction}
      triggeringUser={triggeringUser}
      currentUser={user}
      owner={owner}
      conversationId={conversationId}
      errorMessage={errorMessage}
      isValidating={isValidating}
      isPulsing={isPulsing}
      onValidate={handleValidation}
    />
  );
}
